import React, { useState } from 'react';
import { User } from 'firebase/auth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Logout, Refresh, Brightness4, Brightness7, Edit, Settings, RocketLaunch, Notifications } from '@mui/icons-material';
import UserProfileDialog from './UserProfileDialog';
import NotificationsDialog from './NotificationsDialog';
import UserAutodeployDialog from './UserAutodeployDialog';
import { signOutUser, refreshGitHubPullRequests } from '../firebase';
import { usePullRequests } from '../contexts/PullRequestsContext';
import { useConfig } from '../contexts/ConfigContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { formatDate } from '../utils/deploymentUtils';

interface HeaderProps {
  user: User | null;
}

const Header: React.FC<HeaderProps> = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lastRefreshed } = usePullRequests();
  const { userProfile, flags } = useConfig();
  const canEditConfig = userProfile?.canEditConfig ?? false;
  const githubNotificationsEnabled = flags?.githubCommentNotificationsEnabled === true;
  const { mode, toggleTheme } = useThemeContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const [autodeployDialogOpen, setAutodeployDialogOpen] = useState(false);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      handleUserMenuClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshGitHubPullRequests();
    } catch (err: any) {
      console.error('Error refreshing data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const getUserInitials = (user: User) => {
    if (user.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.email?.charAt(0).toUpperCase() || 'U';
  };

  const navigationItems = [
    { path: '/pull-requests', label: 'Pull Requests', matchPaths: ['/pull-requests', '/pull-request'] },
    { path: '/main-branch', label: 'Main Branch', matchPaths: ['/main-branch'] },
    { path: '/environments', label: 'Environments', matchPaths: ['/environments', '/environment'] },
    { path: '/deployments', label: 'Deployments', matchPaths: ['/deployments', '/deployment'] },
    { path: '/metrics', label: 'Metrics', matchPaths: ['/metrics'] },
  ];

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        <img src="/deli-toast-logo.png" alt="Finvari Deli Logo" style={{ width: 32, height: 32, marginRight: 8 }} />
        <Typography 
          variant="h6" 
          component={Link} 
          to="/" 
          sx={{ 
            flexGrow: 0,
            textDecoration: 'none', 
            color: 'inherit',
            fontWeight: 600,
            mr: 4,
          }}
        >
          Deli
        </Typography>

        {/* Navigation Links */}
        {user && (
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
            {navigationItems.map((item) => {
              const isSelected = item.matchPaths.some(matchPath => 
                location.pathname === matchPath || location.pathname.startsWith(matchPath + '/')
              );
              return (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  variant="outlined"
                  sx={{
                    borderColor: isSelected ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: isSelected ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        )}

        {/* Refresh Section and User Menu */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {lastRefreshed && (
                <Typography variant="caption" color="rgba(255, 255, 255, 0.7)">
                  Last updated: {formatDate(new Date(lastRefreshed).toISOString())}
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh from GitHub'}
              </Button>
            </Box>

            <IconButton
              onClick={toggleTheme}
              color="inherit"
              title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>

            <IconButton
              onClick={handleUserMenuOpen}
              sx={{ p: 0, ml: 1 }}
            >
              <Avatar
                src={userProfile?.photoURL || undefined}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'secondary.main',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {getUserInitials(user)}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
              PaperProps={{
                elevation: 8,
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem disabled>
                <Avatar src={userProfile?.photoURL || undefined} sx={{ bgcolor: 'secondary.main', fontSize: '0.75rem' }}>
                  {getUserInitials(user)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {user.displayName || user.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleUserMenuClose(); setProfileDialogOpen(true); }}>
                <Edit fontSize="small" sx={{ mr: 1 }} />
                Edit Profile
              </MenuItem>
              <MenuItem onClick={() => { handleUserMenuClose(); setNotificationsDialogOpen(true); }}>
                <Notifications fontSize="small" sx={{ mr: 1 }} />
                Notification Settings
              </MenuItem>
              <MenuItem onClick={() => { handleUserMenuClose(); setAutodeployDialogOpen(true); }}>
                <RocketLaunch fontSize="small" sx={{ mr: 1 }} />
                Configure Autodeploy
              </MenuItem>
              {canEditConfig && (
                <MenuItem onClick={() => { handleUserMenuClose(); navigate('/config/edit'); }}>
                  <Settings fontSize="small" sx={{ mr: 1 }} />
                  Edit Global Config
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleSignOut}>
                <Logout fontSize="small" sx={{ mr: 1 }} />
                Sign Out
              </MenuItem>
            </Menu>

            {userProfile && (
              <UserProfileDialog
                open={profileDialogOpen}
                onClose={() => setProfileDialogOpen(false)}
                userProfile={userProfile}
              />
            )}

            {userProfile && (
              <NotificationsDialog
                open={notificationsDialogOpen}
                onClose={() => setNotificationsDialogOpen(false)}
                userProfile={userProfile}
                githubEnabled={githubNotificationsEnabled}
              />
            )}

            {userProfile && (
              <UserAutodeployDialog
                open={autodeployDialogOpen}
                onClose={() => setAutodeployDialogOpen(false)}
                userProfile={userProfile}
              />
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;